<template>
  <div>
    <v-layout align-center>
      <v-flex xs12 sm10 md8 lg6>
        <v-layout wrap>
          <v-flex xs12>
            <div v-if="loading">
              <spinner />
            </div>
            <div v-else>
              <div class="title-screen">
                <span>{{ adminText('workflowsAll.invite_attendees_9c2ffd6', 'Invite attendees') }}</span>
              </div>
              <invite-and-share-categories-section
                v-if="hasCategoriesAndZonesModule && applicationsOpen"
                :category="form.category"
                :predefined-category="form.predefinedCategory"
                :categories="categories"
                :categories-module="extraModules.categoriesAndZones"
                @update:categorySelected="onCategoryChange"
                @update:predefinedCategory="onPredefinedCategoryChange"
              />
              <attendee-invite-form
                :event-slug="eventSlug"
                :event-id="eventId"
                :selected-language="selectedLanguage"
                :form="form"
                :invite-url="inviteUrl"
                :disabled="!applicationsOpen"
                @onSelectedLanguage="onLanguageChange"
                @onShowSnackbar="showSnackbar"
              />
              <event-passwords
                :event-passwords-flag="eventPasswordsFlag"
                :event-id="eventId"
                @onEventPasswordsFlagChange="onEventPasswordsFlagChange"
              />
              <main-button
                v-if="applicationsOpen"
                :text="adminText('workflowsAll.close_event_applications_9eca4ec', 'Close event applications')"
                depressed
                flat
                color="primary"
                @click.prevent="isVisibleCloseEventDialog = true"
              />
              <main-button
                v-if="!applicationsOpen"
                :text="adminText('workflowsAll.open_event_applications_552442a', 'Open event applications')"
                depressed
                @click.prevent="toggleEventApplications(true)"
              />
            </div>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <delete-dialog
      :content="workflowLabel((adminText('workflowsAll.closing_applications_includes_rejecting_all_p_8de0f31', 'Closing applications includes rejecting all pending requests and disables further registrations. Are you sure you want to close applications for this event?')))"
      :on-delete="() => toggleEventApplications(false)"
      :show.sync="isVisibleCloseEventDialog"
      :action-message="adminText('workflowsAll.close_bbfa773', 'Close')"
      :title="adminText('workflowsAll.close_event_applications_9eca4ec', 'Close event applications')"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="workflowLabel(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { GET_EVENT_CATEGORIES, SET_EVENT_PASSWORDS_FLAG } from '../../graphql/event.graphql'
import Spinner from '../shared/spinner'
import AttendeeInviteForm from './attendee-invite-form.vue'
import { toggleEventApplications } from './../../http'
import DeleteDialog from './../shared/dialogs/delete-dialog.vue'
import EventPasswords from '@/components/events/event-passwords.vue'
import InviteAndShareCategoriesSection from '@/components/accreditations/invite-and-share-categories-section.vue'
import { frontendURL } from '@/utils'
import { CREATE_EVENT_CATEGORY_REQUEST } from '@/graphql/eventCategoryRequest.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'invite-attendees',
  components: {
    SimpleSnackbar,
    InviteAndShareCategoriesSection,
    EventPasswords,
    Spinner,
    AttendeeInviteForm,
    DeleteDialog,
  },
  props: {
    eventId: {
      default: null,
      type: String,
    },
  },
  data() {
    return {
      selectedLanguage: 'hr',
      form: {
        url: '',
        zones: [],
        predefinedCategory: false,
        category: null,
      },
      eventSlug: '',
      categories: [],
      extraModules: null,
      applicationsOpen: false,
      isVisibleCloseEventDialog: false,
      eventPasswordsFlag: null,
      loading: false,
      snackbar: {},
    }
  },
  computed: {
    inviteUrl() {
      return this.form.url.length ? `${this.form.url}?lang=${this.selectedLanguage}` : ''
    },
    hasCategoriesAndZonesModule() {
      return this.extraModules && this.extraModules.categoriesAndZones
    },
  },
  async mounted() {
    await this.fetchEventCategories()
  },
  methods: {
    onLanguageChange(language) {
      this.selectedLanguage = language
    },
    showSnackbar(snackbarData) {
      this.snackbar = snackbarData
    },
    getInviteNoCategoriesUrl() {
      return `${frontendURL}/events/${this.eventSlug}/register`
    },
    async getInviteCategoriesUrl(eventId) {
      const variables = {
        eventCategoryRequestInput: {
          allCategories: this.form.predefinedCategory,
          categoryId: this.form.category,
        },
      }

      const { data } = await this.$apollo.mutate({
        mutation: CREATE_EVENT_CATEGORY_REQUEST,
        variables: { eventId, ...variables },
      })

      const { token } = data.createEventCategoryRequest

      return `${frontendURL}/accreditation-invite/${token}`
    },
    async onCategoryChange(categoryId) {
      this.form.category = categoryId

      if (!categoryId) {
        this.form.url = ''

        return
      }

      this.form.url = await this.getInviteCategoriesUrl(this.eventId)
    },
    async onPredefinedCategoryChange(predefinedCategory) {
      this.form.predefinedCategory = predefinedCategory

      if (this.form.predefinedCategory) {
        this.form.category = null
      }

      if (!this.form.predefinedCategory) {
        this.form.url = ''

        return
      }

      this.form.url = await this.getInviteCategoriesUrl(this.eventId)
    },
    async toggleEventApplications(applicationsOpen) {
      await toggleEventApplications({
        eventId: this.eventId,
        applicationsOpen,
        refetchQueries: ['getEventCategories'],
      })

      this.applicationsOpen = applicationsOpen
    },
    async onEventPasswordsFlagChange(flag) {
      await this.$apollo.mutate({
        mutation: SET_EVENT_PASSWORDS_FLAG,
        variables: {
          eventId: this.eventId,
          eventPasswordsFlag: flag,
        },
      })

      await this.fetchEventCategories()
    },
    async fetchEventCategories() {
      this.loading = true
      try {
        const { data } = await this.$apollo.query({
          query: GET_EVENT_CATEGORIES,
          variables: {
            id: this.eventId,
          },
        })

        const {
          getEventCategories: { categories, extraModules, slug, applicationsOpen, flags },
        } = data
        this.eventSlug = slug
        this.categories = categories
        this.extraModules = extraModules
        this.applicationsOpen = applicationsOpen
        this.eventPasswordsFlag = flags && !!flags.eventPasswords

        if (!this.hasCategoriesAndZonesModule) {
          this.form.url = this.getInviteNoCategoriesUrl()
        }

        this.loading = false
      } catch (e) {
        console.error('Error while fetching event categories', e)
        this.snackbar = {
          open: true,
          message: 'workflowsAll.oops_something_went_wrong_74c86ae',
          color: 'error',
        }
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 2rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }
}
</style>
